import { clsx } from "cn";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, type DocumentProps, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@/components/ui/button/button";
import { BwChevronLeftLine, BwChevronRightLine } from "@/components/ui/icons";
import "./pdfjs";

export type PdfViewerProps = DocumentProps & {
  //
};

const PdfViewer = ({
  className,
  onLoadSuccess,
  ...restProps
}: PdfViewerProps) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth * 0.8);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight * 0.8);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleOnDocumentLoadSuccess = useCallback(
    (pdfDocProxy: any) => {
      setNumPages(pdfDocProxy.numPages);
      setPageNumber(1);
      onLoadSuccess?.(pdfDocProxy);
    },
    [onLoadSuccess],
  );

  const onChangePage = useCallback((offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }, []);

  const handleOnClickPreviousPage = useCallback(() => {
    onChangePage(-1);
  }, [onChangePage]);

  const handleOnClickNextPage = useCallback(() => {
    onChangePage(1);
  }, [onChangePage]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth * 0.8);
      setWindowHeight(window.innerHeight * 0.8);
    }, 200);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="relative flex flex-col items-center max-w-full"
    >
      <Document
        className={clsx("w-fit max-w-full group", className)}
        onLoadSuccess={handleOnDocumentLoadSuccess}
        {...restProps}
      >
        <Page
          className="flex flex-col items-center"
          pageNumber={pageNumber}
          renderAnnotationLayer={false}
          {...(windowWidth > windowHeight && {
            height: windowHeight,
          })}
          {...(windowWidth < windowHeight && {
            width: windowWidth,
          })}
          devicePixelRatio={4}
        />
        {Number(numPages) > 1 && (
          <div
            className="absolute bottom-0 z-10 flex items-center justify-between -translate-x-1/2 rounded-sm left-1/2 bg-bg-surface-elevate-low"
            style={{ boxShadow: "0 10px 30px 0 rgb(16 36 94 / 15%)" }}
          >
            <Button
              className="px-3 py-1 text-black/64 disabled:text-black/24"
              disabled={pageNumber === 1}
              onClick={handleOnClickPreviousPage}
              variant="neutral"
              kind="ghost"
              size="icon-sm"
            >
              <BwChevronLeftLine />
            </Button>
            <p className="px-3 py-1 text-black/64">
              {pageNumber} of {numPages}
            </p>
            <Button
              className="px-3 py-1 text-black/64 disabled:text-black/24"
              disabled={pageNumber === numPages}
              onClick={handleOnClickNextPage}
              variant="neutral"
              kind="ghost"
              size="icon-sm"
            >
              <BwChevronRightLine />
            </Button>
          </div>
        )}
      </Document>
    </div>
  );
};

export default PdfViewer;
